


























































































































































// @ts-ignore
import v8n from 'v8n';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { authModule } from '@/store';
import { ApiError, User, isVerificationRequired } from '@/api';
import ChangePassword from '@/components/auth/ChangePassword.vue';
import VerificationGateDialog from '@/components/auth/VerificationGateDialog.vue';

@Component({
  components: { ChangePassword, VerificationGateDialog },
})
export default class extends Vue {
  loading: boolean = false;
  success: boolean = false;
  error: string = '';
  showGate: boolean = false;

  user: User | null = null;

  // The address as the server holds it, captured on mount. `user` is the store's
  // own instance, so `user.email` is already the edited value - there is nothing
  // else to compare against.
  originalEmail: string = '';

  currentPassword: string = '';

  // Set only after a save that moved the address, so the success alert can name
  // where the confirmation link went.
  savedNewEmail: string = '';

  stateOptions = [
    'Australian Capital Territory',
    'New South Wales',
    'Northern Territory',
    'Queensland',
    'South Australia',
    'Tasmania',
    'Victoria',
    'Western Australia',
  ];

  requiredString = [
    (v: string) =>
      v8n()
        .string()
        .not.empty()
        .test(v) || 'This field is required',
  ];

  requiredNumber = [
    (v: string) =>
      v8n()
        .number()
        .greaterThan(0)
        .test(Number(v)) || 'This field is required',
  ];

  phoneRules = [
    (v: string) =>
      v8n()
        .string()
        .not.empty()
        .test(v) || 'Phone number is required',
    (v: string) =>
      v8n()
        .string()
        .pattern(/^[0-9]{10}$/)
        .test(v) || 'Phone number must be 10 digits',
  ];

  emailRules = [
    (v: string) =>
      v8n()
        .string()
        .not.empty()
        .test(v) || 'Email address required',

    (v: string) =>
      v8n()
        .pattern(/\S+@\S+\.\S+/)
        .test(v) || 'Email address must be valid',
  ];

  // Case-insensitive, matching the server: it casefolds the address, so
  // correcting the capitalisation is not a change and must not ask for a
  // password. An emptied field is not a change either - there is no new
  // address to name in the warning or to ask for a password against, and the
  // required rule is what should speak.
  get emailChanged(): boolean {
    const email = this.user ? this.user.email || '' : '';
    if (!email || !this.originalEmail) {
      return false;
    }
    return email.toLowerCase() !== this.originalEmail.toLowerCase();
  }

  async save() {
    if (!this.user) {
      return;
    }

    // Validate on submit rather than disabling Save until the form is valid.
    // Every one of these fields is optional server-side (`blank=True`), so an
    // account created by staff or predating the field can arrive with a blank
    // phone, address or postcode - and Vuetify shows no error on a field the
    // user has not touched. Bound to validity, the button was then dead with
    // nothing on screen to explain it. Clicking now says what is wrong.
    const form = this.$refs.form as any;
    if (form && !form.validate()) {
      return;
    }

    const changingEmail = this.emailChanged;
    if (changingEmail) {
      this.user.currentPassword = this.currentPassword;
    }

    try {
      this.loading = true;
      await this.user.save();
      this.error = '';
      this.success = true;
      this.savedNewEmail = changingEmail ? this.user.email : '';
      this.originalEmail = this.user.email;
      this.currentPassword = '';
    } catch (e) {
      this.success = false;
      // The gate refuses profile edits too, once the grace window closes, and
      // it is not a complaint about the form - so it gets the same popup the
      // bid failure gets rather than a red banner the user cannot act on.
      if (isVerificationRequired(e)) {
        this.error = '';
        this.showGate = true;
      } else {
        this.error = e.response.errors[0].detail;
      }
    } finally {
      this.loading = false;
      // Never leave the password on the instance: it is a real attribute, so it
      // would be sent again on the next, unrelated save. The typed value stays
      // in the field after a failure so it can be corrected.
      if (this.user) {
        this.user.currentPassword = '';
      }
    }
  }

  async mounted() {
    this.user = authModule.user;
    this.originalEmail = this.user ? this.user.email : '';
  }
}
